import { useNotification } from '@topcon/topcon-utils';
import { useTranslation } from 'react-i18next';

import { ServiceAgreementStatusEnum, type ServiceAgreementBody } from '../../../api/generated/pm';
import ConfirmDeletionModal from '../../Modal/ConfirmDeletionModal';
import useServiceAgreement from '../../../hooks/useServiceAgreement';
import { useCancelServiceAgreementRequest } from '../../../pages/Customers/customers.mutations';
import { useErrorDrawer } from '../../../providers/ErrorDrawerContext/error-drawer.context';

interface TerminateAgreementModalProps {
  serviceAgreement: ServiceAgreementBody;
  /**
   * Which backend object the row is: a `'proxy'` Business Partner Request
   * (Customer does not exist yet; withdrawn via
   * `deleteV1RequestBusinessPartnerById`) or an `'agreement'` with an
   * existing Customer (ended via `deleteV1ServiceAgreementById`). The caller
   * knows this from the endpoint it listed the row from; it cannot be read
   * off `serviceAgreement`.
   */
  kind: 'proxy' | 'agreement';
  open: boolean;
  onClose: (shouldUpdate: boolean) => void;
}

const TerminateCancelServiceAgreementModal = ({
  serviceAgreement,
  kind,
  open,
  onClose,
}: TerminateAgreementModalProps) => {
  const { t } = useTranslation();
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();
  const { deleteServiceAgreement, isPendingCancelServiceAgreement } = useServiceAgreement();
  const {
    mutateAsync: cancelServiceAgreementRequest,
    isPending: isPendingCancelServiceAgreementRequest,
  } = useCancelServiceAgreementRequest();

  // The endpoint follows `kind`; the labels follow status. The api 'cancels' a
  // proxy request and 'deletes' an agreement regardless of status, while the
  // ui says 'cancel' for pending rows and 'delete' for active ones regardless
  // of kind, since that is the action the user sees themselves taking.
  const agreementStatus = serviceAgreement.serviceAgreementStatus;
  const computedLabelType =
    agreementStatus === ServiceAgreementStatusEnum.ACTIVE ? 'delete' : 'cancel';

  const modalOptions: { title: string; description: string; primaryButtonText: string } =
    computedLabelType === 'delete'
      ? {
          title: t('terminateAgreementModal.title'),
          description: t('terminateAgreementModal.description'),
          primaryButtonText: t('terminateAgreementModal.title'),
        }
      : {
          title: t('cancelAgreementModal.title'),
          description: t('cancelAgreementModal.description'),
          primaryButtonText: t('cancelAgreementModal.title'),
        };

  const { customer } = serviceAgreement;
  const address = `${customer?.companyAddress.addressLineOne}, ${customer?.companyAddress.zipPostalCode} ${customer?.companyAddress.city} ${customer?.companyAddress.stateProvinceRegion}, ${customer?.companyAddress.country}`;

  const handleTerminateAgreement = async () => {
    if (!serviceAgreement.customer) return;

    try {
      if (kind === 'agreement') {
        await deleteServiceAgreement(serviceAgreement);
      } else {
        await cancelServiceAgreementRequest(serviceAgreement.id);
      }
      onClose(true);
    } catch (error: unknown) {
      if (!showError(error)) {
        const customerName = serviceAgreement.customer?.name ?? '';
        const { title, description } =
          computedLabelType === 'delete'
            ? {
                title: t('cancelAgreementModal.messages.errorDeleteTitle'),
                description: t('cancelAgreementModal.messages.errorDeleteDescription', {
                  name: customerName,
                }),
              }
            : {
                title: t('cancelAgreementModal.messages.errorRequestTitle'),
                description: t('cancelAgreementModal.messages.errorRequestDescription', {
                  name: customerName,
                }),
              };

        notificationService.addNotification(title, description, 'error');
      }
    }
  };

  return (
    <ConfirmDeletionModal
      title={modalOptions.title}
      description={modalOptions.description}
      data={[
        {
          id: serviceAgreement.id,
          text: serviceAgreement.customer?.name ?? '',
          subText: address,
        },
      ]}
      primaryButtonText={modalOptions.primaryButtonText}
      open={open}
      iconName={'business'}
      onSubmit={handleTerminateAgreement}
      onClose={() => onClose(false)}
      loading={isPendingCancelServiceAgreement || isPendingCancelServiceAgreementRequest}
    />
  );
};

export default TerminateCancelServiceAgreementModal;
